import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { PaginationPrimary } from '@lambdacurry/component-library';
import { LayoutContext } from '../../../components/Layout/LayoutWrapper';
import Store from '../../../store';
import { downloadFile } from '../../../util/file';
import { AuditLogDashboardData, AuditLogFilters as AuditLogFiltersModel } from '../../../types/AuditLogs';
import {
    applyAuditLogFilters,
    getAuditLogActionCategoryOptions,
    getDefaultAuditLogFilters,
    normalizeAuditLogs
} from '../auditLogFilters';
import { buildAuditLogCsv } from '../auditLogCsv';
import { AuditLogFilters } from './AuditLogFilters';
import { AuditLogTable } from './AuditLogTable';
import '../../../components/Audit/audit_log.scss';

export interface AuditLogDashboardProps {
    store: Store;
}

const emptyAuditLogData: AuditLogDashboardData = {
    users: [],
    companies: [],
    apps: [],
    crmConfigs: [],
    publicRoles: [],
    emailTemplates: []
};

export const AuditLogDashboard = ({ store }: AuditLogDashboardProps) => {
    const layoutContext = useContext(LayoutContext);
    const allAccessibleCompanies = layoutContext?.accessibleCompanies?.length
        ? layoutContext.accessibleCompanies
        : store.accessibleCompanies || [];
    const scopedAgencyId = layoutContext?.activeAgencyId || store.agencyStore.activeAgencyId;
    const scopedCompanyId = layoutContext?.activeCompanyId || store.activeCompanyId;
    const scopedCompanies = scopedAgencyId
        ? allAccessibleCompanies.filter(company => company.agency_id === scopedAgencyId)
        : allAccessibleCompanies;

    const [selectedCompanyId, setSelectedCompanyId] = useState<number | undefined>(scopedCompanyId || undefined);
    const [filters, setFilters] = useState<AuditLogFiltersModel>(getDefaultAuditLogFilters);
    const [auditData, setAuditData] = useState<AuditLogDashboardData>(emptyAuditLogData);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | undefined>();

    const allRows = useMemo(() => normalizeAuditLogs(auditData), [auditData]);
    const filteredRows = useMemo(() => applyAuditLogFilters(allRows, filters), [allRows, filters]);
    const actionCategoryOptions = useMemo(() => getAuditLogActionCategoryOptions(allRows), [allRows]);

    const PAGE_SIZE = 50;
    const [currentPage, setCurrentPage] = useState(1);
    const pagesCount = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));

    useEffect(() => {
        setCurrentPage(1);
    }, [filters, selectedCompanyId, scopedAgencyId, scopedCompanyId, allRows]);

    useEffect(() => {
        if (currentPage > pagesCount) {
            setCurrentPage(pagesCount);
        }
    }, [currentPage, pagesCount]);

    const pagedRows = useMemo(
        () => filteredRows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
        [filteredRows, currentPage]
    );

    const loadAuditLogs = useCallback(
        async (queryParams: { agencyId?: number; companyId?: number }) => {
            setLoading(true);
            setError(undefined);
            setAuditData(emptyAuditLogData);

            try {
                const result = await store.fetchAuditDashboard(queryParams);
                setAuditData(result);
            } catch {
                setError('Unable to load audit log entries right now.');
            } finally {
                setLoading(false);
            }
        },
        [store]
    );

    useEffect(() => {
        if (scopedCompanyId) {
            setSelectedCompanyId(scopedCompanyId);
            setFilters(getDefaultAuditLogFilters());
            setError(undefined);
            return;
        }

        setSelectedCompanyId(undefined);
        setFilters(getDefaultAuditLogFilters());
        setError(undefined);
    }, [scopedCompanyId, scopedAgencyId]);

    useEffect(() => {
        if (scopedCompanyId) {
            void loadAuditLogs({ companyId: scopedCompanyId });
            return;
        }

        if (selectedCompanyId) {
            void loadAuditLogs({ companyId: selectedCompanyId });
            return;
        }

        void loadAuditLogs(scopedAgencyId ? { agencyId: scopedAgencyId } : {});
    }, [loadAuditLogs, scopedAgencyId, scopedCompanyId, selectedCompanyId]);

    const handleCompanyChange = (companyId?: number) => {
        setSelectedCompanyId(companyId);
        setFilters(getDefaultAuditLogFilters());
        setError(undefined);
    };

    const handleExportCsv = () => {
        if (!filteredRows.length) {
            return;
        }

        downloadFile(
            buildAuditLogCsv(filteredRows),
            `audit-logs-${moment.utc().format('YYYYMMDD-HHmmss')}.csv`
        );
    };

    return (
        <div className="audit-log-page">
            <header className="audit-log-header">
                <h1>Audit Logs</h1>
            </header>

            <AuditLogFilters
                accessibleCompanies={scopedCompanies}
                filters={filters}
                actionCategoryOptions={actionCategoryOptions}
                selectedCompanyId={selectedCompanyId}
                showCompanySelector
                canExport={!loading && !error && filteredRows.length > 0}
                onCompanyChange={handleCompanyChange}
                onFiltersChange={setFilters}
                onResetFilters={() => setFilters(getDefaultAuditLogFilters())}
                onExportCsv={handleExportCsv}
            />

            <div className="audit-log-summary">
                <strong>{filteredRows.length}</strong> matching audit entr{filteredRows.length === 1 ? 'y' : 'ies'}
                {filteredRows.length > PAGE_SIZE ? (
                    <span className="audit-log-summary-page">
                        {' '}
                        — showing {(currentPage - 1) * PAGE_SIZE + 1}-
                        {Math.min(currentPage * PAGE_SIZE, filteredRows.length)}
                    </span>
                ) : null}
            </div>
            <AuditLogTable rows={pagedRows} loading={loading} error={error} />
            {!loading && !error && pagesCount > 1 ? (
                <div className="audit-log-pagination">
                    <PaginationPrimary
                        pagesCount={pagesCount}
                        page={currentPage}
                        onChange={(_event, nextPage) => setCurrentPage(nextPage)}
                    />
                </div>
            ) : null}
        </div>
    );
};
